import { MIN_WORD_LENGTH, type GameVariant } from '../../shared/config';
import type { GameMode } from '../../shared/modes';
import { DEBUFF_KINDS, POWER_UP_KINDS, type PowerUpKind } from '../../shared/power-ups';

/** The themed illustration drawn above a page (`onboarding/TutorialArt.svelte`). */
export type TutorialArt = 'trace' | 'score' | 'clock' | 'power-up' | 'boosts' | 'debuffs';

export interface TutorialPage {
  title: string;
  text: string;
  art: TutorialArt;
  /** Power-ups the page defines, listed beneath its text. */
  powerUps?: readonly PowerUpKind[];
}

export const VARIANT_TITLES: Record<GameVariant, string> = { classic: 'Classic', chaos: 'Chaos' };

const BOOST_KINDS = POWER_UP_KINDS.filter((kind) => !(DEBUFF_KINDS as readonly PowerUpKind[]).includes(kind));

export const TUTORIAL_PAGES: Record<GameVariant, readonly TutorialPage[]> = {
  classic: [
    {
      title: 'Trace a word',
      text: 'Drag across neighboring letters, diagonals included, then lift your finger to submit. Each tile can be used once per word.',
      art: 'trace',
    },
    {
      title: 'Longer words score more',
      text: `Words need at least ${MIN_WORD_LENGTH} letters, and Qu counts as two. Each word scores once, so go long for the big points.`,
      art: 'score',
    },
    {
      title: 'Beat the clock',
      text: 'Find as many words as you can before time runs out. Against other players, words only you found earn a 1.5× bonus.',
      art: 'clock',
    },
  ],
  chaos: [
    {
      title: 'Grab power-ups',
      text: 'Power-ups appear on tiles for a few seconds. Trace a word through one to claim it. Classic scoring still applies.',
      art: 'power-up',
    },
    {
      title: 'Help yourself',
      text: 'These give you an edge.',
      art: 'boosts',
      powerUps: BOOST_KINDS,
    },
    {
      title: 'Hit everyone else',
      text: 'These land on every other player.',
      art: 'debuffs',
      powerUps: DEBUFF_KINDS,
    },
  ],
};

/**
 * Where a mode teaches a new player: the last action the player takes before a round, so the
 * pages never eat a timed window (ready check, ranked join deadline, countdown).
 * - `setup`: Play on the mode's setup screen. Restoring a prepared room by URL does not teach.
 * - `room`: creating or joining the room. The host is taught again at Start and a guest in the
 *   lobby when the host changes the variant (`app/lobby-rules.ts`).
 * - `queue`: Find a match, before the Matchmaker can pair the player. Joining the match does not teach.
 * A new mode must choose its checkpoint here and gate that action with `RulesGate.explain`.
 */
export type RulesCheckpoint = 'setup' | 'room' | 'queue';

export const RULES_CHECKPOINT: Record<GameMode, RulesCheckpoint> = {
  daily: 'setup',
  casual: 'setup',
  endless: 'setup',
  practice: 'setup',
  versus: 'room',
  competitive: 'queue',
};

/**
 * The variants to teach before a round of `variant`, in order. Chaos builds on Classic, so a
 * player new to both learns Classic first; an empty list means nothing is left to explain.
 */
export function variantsToTeach(
  variant: GameVariant,
  seen: (variant: GameVariant) => boolean,
): GameVariant[] {
  const required: GameVariant[] = variant === 'chaos' ? ['classic', 'chaos'] : ['classic'];
  return required.filter((candidate) => !seen(candidate));
}
